import { useState } from 'react';
import { Network, Mail, Save } from 'lucide-react';
import Modal from '../common/Modal';
import { useToast } from '../common/ToastContainer';
import { CHANNEL_STATUS } from '../../services/channel.service';

export interface ChannelFormData {
  id: string;
  name: string;
  slug?: string;
  description?: string;
  contactEmail?: string;
  status: number;
}

interface ChannelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (channel: ChannelFormData) => void;
  channel?: ChannelFormData;
  mode: 'add' | 'edit';
}

const emptyChannel: ChannelFormData = {
  id: '',
  name: '',
  slug: '',
  description: '',
  contactEmail: '',
  status: CHANNEL_STATUS.ACTIVE,
};

/**
 * Create/edit a channel. SuperAdmin-only — a ChannelAdmin never reaches this,
 * since being able to create channels or edit their own would let them widen
 * their own scope. The caller is responsible for that gate.
 */
const ChannelModal = ({ isOpen, onClose, onSave, channel, mode }: ChannelModalProps) => {
  const toast = useToast();
  // Seeded once per mount. Callers pass a `key` that changes when the target
  // channel changes, so the form re-initialises by remounting rather than by
  // an effect that writes state on every open.
  const [formData, setFormData] = useState<ChannelFormData>(channel || emptyChannel);

  const handleChange = (field: keyof ChannelFormData, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (!formData.name || formData.name.trim().length === 0) {
      toast.error('Validation Error', 'Channel name is required');
      return;
    }

    if (formData.contactEmail && formData.contactEmail.trim().length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.contactEmail)) {
        toast.error('Validation Error', 'Please enter a valid contact email address');
        return;
      }
    }

    onSave(formData);
    onClose();
  };

  const footer = (
    <div className="flex items-center justify-end space-x-3">
      <button onClick={onClose} className="btn-secondary">
        Cancel
      </button>
      <button onClick={handleSubmit} className="btn-primary inline-flex items-center space-x-2">
        <Save className="w-4 h-4" />
        <span>{mode === 'add' ? 'Create Channel' : 'Save Changes'}</span>
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'add' ? 'Add New Channel' : `Edit Channel: ${channel?.name}`}
      size="2xl"
      footer={footer}
    >
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
            <Network className="w-5 h-5 text-primary-600" />
            <span>Channel Details</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Channel Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., APAC Partners"
                className="input-base w-full"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Slug</label>
              <input
                type="text"
                value={formData.slug || ''}
                onChange={(e) => handleChange('slug', e.target.value)}
                placeholder="e.g., apac-partners"
                className="input-base w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                Optional short identifier. Generated from the name if left blank.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-1">
                <Mail className="w-4 h-4 text-gray-400" />
                <span>Contact Email</span>
              </label>
              <input
                type="email"
                value={formData.contactEmail || ''}
                onChange={(e) => handleChange('contactEmail', e.target.value)}
                placeholder="e.g., partners@example.com"
                className="input-base w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={formData.status}
                onChange={(e) => handleChange('status', Number(e.target.value))}
                className="input-base w-full"
              >
                <option value={CHANNEL_STATUS.ACTIVE}>Active</option>
                <option value={CHANNEL_STATUS.SUSPENDED}>Suspended</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Optional notes about this channel partner..."
                className="input-base w-full"
                rows={3}
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ChannelModal;
